import React, { useState } from "react";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { presaleCheckLink, presaleCoreLink, presaleSignatureLink } from "../../constant";
import { useExchangeRate, toKRWLabel } from "../../hooks/useExchangeRate";

const currentMonth = `${new Date().getMonth() + 1}월`;

// ── Types ────────────────────────────────────────────────────────────────────

interface ServiceCardProps {
  topBorder?: string;
  badgeText: string;
  badgeClass: string;
  name: string;
  price: string;
  krwLabel?: string;
  tagline: string;
  features: string[];
  forWhom: string[];
  cta: string;
  ctaClass: string;
  link: string;
  popular?: boolean;
}

interface ReviewProps {
  quote: string;
  service: "진단" | "핵심" | "시그니처";
}

interface FaqItem {
  q: string;
  a: string;
}

// ── Data ─────────────────────────────────────────────────────────────────────

const services: ServiceCardProps[] = [
  {
    badgeText: "첫 번째 단계",
    badgeClass: "bg-gray-100 text-gray-500",
    name: "진단 서비스",
    price: "£59",
    tagline: "어디서부터 시작해야 할지 모르겠다면",
    features: [
      "CV가 영국 시장에서 어떻게 읽히는지 점검",
      "목표 포지션 적합도 분석",
      "지금 당장 손봐야 할 우선순위 정리",
      "5페이지 분량 진단 피드백 리포트",
    ],
    forWhom: [
      "준비 방향이 아직 안 잡힌 분",
      "CV 수정 전 내 커리어 좌표를 먼저 확인하고 싶은 분",
    ],
    cta: "내 커리어 좌표 확인하기",
    ctaClass: "border border-gray-300 text-gray-700 hover:bg-gray-700 hover:text-white",
    link: presaleCheckLink,
  },
  {
    badgeText: "서류 집중",
    badgeClass: "bg-blue-50 text-blue-600",
    name: "핵심 리빌딩 서비스",
    price: "£149",
    tagline: "실제 지원 가능한 서류 퀄리티가 필요한 단계",
    features: [
      "CV 문단별 전략적 테일러링",
      "강점·개선 포인트 Before-After 피드백 리포트",
      "링크드인 섹션별 가이드 + 네트워킹 전략",
    ],
    forWhom: [
      "방향은 잡혔고 지원 직전인 분",
      "CV와 링크드인을 채용담당자 시선으로 정비하고 싶은 분",
    ],
    cta: "CV·링크드인 전략 정비하기",
    ctaClass: "border border-blue-500 text-blue-600 hover:bg-blue-600 hover:text-white",
    link: presaleCoreLink,
  },
  {
    topBorder: "border-t-4 border-accent",
    badgeText: "가장 폭넓은 서비스",
    badgeClass: "bg-accent/20 text-main",
    name: "시그니처 서비스",
    price: "£299",
    tagline: "서류부터 인터뷰까지 한 번에 준비하고 싶다면",
    features: [
      "CV + 커버레터 문단별 테일러링 + 피드백 리포트",
      "링크드인 프로필 가이드 + 네트워킹 전략",
      "STAR 기법 인터뷰 가이드 (강점 기반 답변 리스트)",
      "첨삭 후 1회 리비전 피드백 가이드",
    ],
    forWhom: [
      "서류와 인터뷰를 따로따로 준비하고 싶지 않은 분",
      "영국 취업 준비 전체 흐름을 한 번에 정리하고 싶은 분",
    ],
    cta: "서류부터 인터뷰까지 준비하기",
    ctaClass: "bg-gold text-main font-bold hover:brightness-105",
    link: presaleSignatureLink,
    popular: true,
  },
];

const reviews: ReviewProps[] = [
  {
    quote: "반신반의했지만 한 달 안에 서류합격 4곳을 했어요.",
    service: "시그니처",
  },
  {
    quote: "피드백 받은 대로 수정하니 바로 링크드인 메시지와 노출이 늘었어요.",
    service: "시그니처",
  },
  {
    quote: "챗GPT로 서류탈락을 많이 경험했었는데, 영국식 맞춤형은 다르네요. 구체적인 문장을 예시로 주셔서 헤매지 않고 수정했어요.",
    service: "핵심",
  },
  {
    quote: "몇 달간 없던 반응, 수정 후 달라졌어요. 지원 직무를 분명히 정하니 방향이 명확해 졌어요.",
    service: "진단",
  },
];

const faqs: FaqItem[] = [
  {
    q: "어떤 서비스를 먼저 선택해야 할지 모르겠어요.",
    a: "방향과 현재 상태를 먼저 점검하고 싶다면 진단이 적합합니다. 이미 방향은 어느 정도 잡혀 있고 실제 지원용 서류 완성도를 높이는 것이 더 중요하다면 핵심 리빌딩이 맞습니다. 서류부터 인터뷰까지 전체 흐름을 한 번에 준비하고 싶다면 시그니처를 추천합니다.",
  },
  {
    q: "진단 후 핵심이나 시그니처로 이어서 진행할 수 있나요?",
    a: "네. 현재 상태를 먼저 확인한 뒤 다음 단계가 필요하다고 판단되면 핵심 리빌딩이나 시그니처로 이어서 준비할 수 있습니다.",
  },
  {
    q: "핵심 리빌딩과 시그니처의 가장 큰 차이는 무엇인가요?",
    a: "핵심 리빌딩은 서류 중심의 전략적 정비에 집중한 서비스입니다. 시그니처는 여기에 더해 커버레터와 인터뷰 준비까지 포함한 통합 준비 서비스입니다.",
  },
  {
    q: "아직 지원 전 단계인데 핵심이나 시그니처를 선택해도 되나요?",
    a: "가능합니다. 다만 아직 방향 점검이 더 필요한 상태라면 진단부터 시작하는 편이 더 효율적일 수 있습니다.",
  },
  {
    q: "결제 후 진행은 어떻게 되나요?",
    a: "결제 후 신청서 양식이 안내됩니다. 필요한 정보와 파일을 제출해주시면 영업일 기준 내 결과물을 전달드립니다.",
  },
];

// ── Sub-components ────────────────────────────────────────────────────────────

function ServiceCard({
  topBorder,
  badgeText,
  badgeClass,
  name,
  price,
  krwLabel,
  tagline,
  features,
  forWhom,
  cta,
  ctaClass,
  link,
  popular,
}: ServiceCardProps) {
  return (
    <div
      className={`relative flex flex-col rounded-2xl bg-white overflow-hidden ${
        popular ? `shadow-lg ${topBorder ?? ""}` : "shadow-sm"
      }`}
    >
      {popular && (
        <div className="bg-main text-center py-1.5">
          <span className="text-[10px] font-bold text-gold tracking-[2px] uppercase">
            Most Popular
          </span>
        </div>
      )}

      <div className="p-5 sm:p-6 flex flex-col flex-1 gap-4">
        {/* Header */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${badgeClass}`}>
              {badgeText}
            </span>
            <div className="text-right">
              <span className="text-2xl font-extrabold text-main">{price}</span>
              {krwLabel && <p className="text-xs text-gray-400 mt-0.5">{krwLabel}</p>}
            </div>
          </div>
          <h3 className="text-base font-bold text-main mb-1">{name}</h3>
          <p className="text-xs text-gray-500 leading-relaxed">{tagline}</p>
        </div>

        {/* Features */}
        <ul className="space-y-2">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-gray-700">
              <Check size={12} className="mt-0.5 text-main shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {/* For whom */}
        <div className="bg-gray-50 rounded-xl p-3.5">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2">
            이런 분께
          </p>
          <ul className="space-y-1">
            {forWhom.map((w, i) => (
              <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                <span className="shrink-0 mt-0.5">·</span>
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className={`mt-auto block text-center py-3 px-5 rounded-xl text-sm font-semibold transition-all ${ctaClass}`}
        >
          {cta}
        </a>
      </div>
    </div>
  );
}

// Speech bubble tail color keyed by service
const reviewStyle: Record<ReviewProps["service"], { bubbleBg: string; tailColor: string; badgeText: string; badgeCls: string }> = {
  시그니처: {
    bubbleBg: "bg-main",
    tailColor: "#0A1A3E",
    badgeText: "시그니처",
    badgeCls: "text-main bg-gold",
  },
  핵심: {
    bubbleBg: "bg-white",
    tailColor: "#ffffff",
    badgeText: "핵심",
    badgeCls: "text-blue-600 bg-blue-50",
  },
  진단: {
    bubbleBg: "bg-white",
    tailColor: "#ffffff",
    badgeText: "진단",
    badgeCls: "text-gray-600 bg-gray-100",
  },
};

function SpeechBubble({ quote, service }: ReviewProps) {
  const { bubbleBg, tailColor, badgeText, badgeCls } = reviewStyle[service];
  const isInverted = service === "시그니처";

  return (
    <div className="flex flex-col">
      {/* Bubble */}
      <div className={`relative ${bubbleBg} rounded-2xl rounded-bl-sm px-5 py-5 shadow-sm`}>
        <p className={`text-sm leading-relaxed font-medium ${isInverted ? "text-white/90" : "text-gray-800"}`}>
          "{quote}"
        </p>
        {/* Triangle tail — bottom-left */}
        <div
          className="absolute left-5 -bottom-3"
          style={{
            width: 0,
            height: 0,
            borderRight: "14px solid transparent",
            borderTop: `14px solid ${tailColor}`,
          }}
        />
      </div>
      {/* Attribution */}
      <div className="mt-5 pl-3 flex items-center gap-2">
        <span className="text-gray-400 text-sm font-light">—</span>
        <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${badgeCls}`}>
          {badgeText}
        </span>
      </div>
    </div>
  );
}

function FaqRow({ q, a }: FaqItem) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        className="w-full flex items-start justify-between py-4 text-left gap-4"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-sm font-semibold text-main leading-relaxed">{q}</span>
        {open ? (
          <ChevronUp size={15} className="shrink-0 mt-0.5 text-gray-400" />
        ) : (
          <ChevronDown size={15} className="shrink-0 mt-0.5 text-gray-400" />
        )}
      </button>
      {open && (
        <p className="pb-4 text-sm text-gray-600 leading-relaxed">{a}</p>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const PresalePage: React.FC = () => {
  const { rate } = useExchangeRate();

  const withKrw = (s: ServiceCardProps): ServiceCardProps => {
    const gbp = parseFloat(s.price.replace("£", ""));
    return { ...s, krwLabel: rate && gbp > 0 ? toKRWLabel(gbp * rate) : undefined };
  };

  return (
    <div className="min-h-screen bg-sub font-sans">

      {/* ── Hero ── */}
      <section className="bg-main text-white px-6 py-14 sm:py-20 text-center">
        <p className="text-gold text-[10px] font-bold tracking-[3px] uppercase mb-4">
          BeMe Consulting
        </p>
        <h1 className="text-[1.75rem] sm:text-3xl md:text-4xl font-extrabold leading-tight mb-5">
          영국 취업 준비,<br />
          지금 필요한 건<br />
          <span className="text-gold">내 단계에 맞는 준비</span>입니다.
        </h1>
        <p className="text-sm text-white/70 leading-relaxed max-w-sm mx-auto mb-8">
          어디서부터인지 모르면 <strong className="text-white">진단</strong>,&nbsp;
          서류 정비가 목표면 <strong className="text-white">핵심</strong>,&nbsp;
          전부 한 번에면 <strong className="text-white">시그니처</strong>.
        </p>
        <p className="text-[11px] text-white/40">지금 내 상황에 맞는 단계부터 선택해보세요.</p>
      </section>

      {/* ── Service Cards ── */}
      <section className="px-4 sm:px-6 py-10 sm:py-14 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
          {services.map((s) => (
            <ServiceCard key={s.name} {...withKrw(s)} />
          ))}
        </div>
      </section>

      {/* ── Selection Guide ── */}
      <section className="bg-white px-5 sm:px-6 py-10">
        <div className="max-w-xl mx-auto">
          <h2 className="text-base font-bold text-main text-center mb-6">
            어떤 서비스가 맞는지 헷갈린다면
          </h2>
          <div className="space-y-3">
            {[
              {
                cond: "어디서부터 시작해야 할지 모르겠다",
                result: "진단",
                link: presaleCheckLink,
                cls: "text-gray-600 bg-gray-100",
              },
              {
                cond: "방향은 잡혔고 실제 지원 서류가 필요하다",
                result: "핵심 리빌딩",
                link: presaleCoreLink,
                cls: "text-blue-600 bg-blue-50",
              },
              {
                cond: "서류·링크드인·인터뷰를 한 번에 준비하고 싶다",
                result: "시그니처",
                link: presaleSignatureLink,
                cls: "text-main bg-gold/30",
              },
            ].map(({ cond, result, link, cls }) => (
              <div
                key={result}
                className="flex flex-col sm:flex-row sm:items-center justify-between bg-gray-50 rounded-xl px-4 py-3.5 gap-2"
              >
                <p className="text-sm text-gray-700">{cond}</p>
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`self-start sm:self-auto shrink-0 text-xs font-bold px-3 py-1.5 rounded-full ${cls}`}
                >
                  → {result}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Reviews ── */}
      <section className="px-4 sm:px-6 py-12 sm:py-16 max-w-4xl mx-auto">
        <h2 className="text-base font-bold text-main text-center mb-10">실제 후기</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 sm:gap-8">
          {reviews.map((r, i) => (
            <SpeechBubble key={i} {...r} />
          ))}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="bg-white px-5 sm:px-6 py-10">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-base font-bold text-main text-center mb-6">자주 묻는 질문</h2>
          <div className="rounded-2xl border border-gray-100 px-4 sm:px-6">
            {faqs.map((faq, i) => (
              <FaqRow key={i} {...faq} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Urgency CTA ── */}
      <section className="bg-main px-5 sm:px-6 py-14 sm:py-16 text-center">
        <p className="text-gold text-[10px] font-bold tracking-[3px] uppercase mb-4">마감 임박</p>
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight mb-4">
          {currentMonth} 문의 마감 전에<br />지금 신청하세요.
        </h2>
        <p className="text-white/60 text-sm leading-relaxed max-w-xs mx-auto mb-2">
          매달 받을 수 있는 신청 수가 정해져 있습니다.<br />
          준비가 됐다면 지금이 가장 빠른 시작입니다.
        </p>
        <p className="text-white/30 text-xs mb-10">자리가 마감되면 다음 달로 이월됩니다.</p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-lg mx-auto">
          <a
            href={presaleCheckLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 py-4 px-4 rounded-xl bg-white/10 border border-white/20 text-white text-sm font-semibold hover:bg-white/20 transition-all text-center"
          >
            커리어 좌표 확인하기
          </a>
          <a
            href={presaleCoreLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 py-4 px-4 rounded-xl bg-white/10 border border-white/20 text-white text-sm font-semibold hover:bg-white/20 transition-all text-center"
          >
            CV·링크드인 정비하기
          </a>
          <a
            href={presaleSignatureLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 py-4 px-4 rounded-xl bg-gold text-main text-sm font-bold hover:brightness-105 transition-all text-center"
          >
            서류부터 인터뷰까지
          </a>
        </div>
      </section>

      {/* ── Mini Footer ── */}
      <footer className="text-center py-6 text-xs text-gray-400 bg-sub">
        © BeMe Consulting — Be Yourself, Be Authentic
      </footer>
    </div>
  );
};

export default PresalePage;
